import _ from "lodash";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";

import {
  SearchInput,
  ChecklistDropdown,
  FilterLabelButton,
} from "@toddle-edu/ds-web";

import { useI18n } from "Hooks";
import classes from "./UnitFilters.scss";
import {
  updateFilters,
  updateSelectedUnit,
} from "AppComponents/LEIntoUnitImport/modules/Module";
import {
  getOrganizationSubjectsQuery,
  getOrganizationStaffQuery,
  getStaffCoursesQuery,
  getOrganizationCoursesQuery,
} from "AppComponents/LEIntoUnitImport/modules/Queries";
import {
  getOrganizationSubjectsQueryFromCache,
  getOrganizationStaffQueryFromCache,
  getStaffCoursesFromCache,
} from "AppComponents/LEIntoUnitImport/modules/GraphqlHelpers";
import {
  isDPLibraryRoute,
  getCurriculumProgramIdFromType,
  checkHasLibrarySuperAdminAccess,
  getCurriculumProgramsWithAllClassAccess,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { getOrganizationCurriculumPrograms } from "modules/CommonSelectors";
import { isSchoolLibraryRoute } from "Teacher/routes/SchoolLibrary/modules/Utils";

import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

const styles = {
  labelStyle: {
    maxWidth: "130px",
  },
};

const EMPTY_ARRAY = [];

const getCurriculumUnitTypes = ({ unitTypes, curriculumType }) => {
  return _.filter(
    unitTypes,
    ({ curriculum }) => curriculum.type === curriculumType
  );
};

const getCurriculumUnitTypesMemoized = _.memoize(
  params => getCurriculumUnitTypes(params),
  params => JSON.stringify(params)
);

const UnitFilters = ({
  updateFilters,
  gradeId,
  unitTypes = EMPTY_ARRAY,
  selectedFilters,
  updateSelectedUnit,
  subjectList = EMPTY_ARRAY,
  staffList = EMPTY_ARRAY,
  isDPLibrary,
  isDPSchoolLibrary,
  courses,
}) => {
  const { t } = useI18n();

  const [searchInput, setSearchInput] = useState("");

  const unitTypeOptions = useMemo(() => {
    const options = _.map(unitTypes, unitType => ({
      value: unitType.id,
      label: unitType.feedLabel,
      key: unitType.id,
    }));

    return options;
  }, [unitTypes]);

  const subjectOptions = useMemo(() => {
    const options = _.map(subjectList, subject => ({
      value: subject.id,
      label: subject.name,
      key: subject.id,
    }));

    return options;
  }, [subjectList]);

  const courseOptions = _.map(courses, course => ({
    value: _.get(course, "node.id"),
    label: _.get(course, "node.title"),
    key: _.get(course, "node.id"),
  }));

  const creatorOptions = useMemo(() => {
    const options = _.map(staffList, ({ node }) => ({
      value: node.id,
      label: getFormattedUserName({ user: node, type: "staff" }),
      key: node.id,
    }));

    return options;
  }, [staffList]);

  const coursesFilterPropOptions = {
    isSearchEnabled: true,
    options: courseOptions,
    showSelectedOptionsOnTop: false,
    value: selectedFilters.courses,
    showAllOption: true,
    labelStyle: styles.labelStyle,
    label: t("common:class_plural"),
    labelValue: getLabelValueForMultiFilterLabelButton({
      options: courseOptions,
      selectedOptions: selectedFilters.courses,
      noneText: t("common:select"),
      allText: t("common:all"),
    }),
    onChange: courses => updateFilterValue({ type: "courses", value: courses }),
  };

  // On switching grade, clear the search text
  useEffect(() => {
    setSearchInput("");
  }, [gradeId]);

  const debouncedUpdateSearchTextRedux = useRef(
    _.debounce(value => {
      updateFilters({ searchText: value });
    }, 60)
  ).current;

  const onSearchTextChange = value => {
    setSearchInput(value);
    debouncedUpdateSearchTextRedux(value);
    updateSelectedUnit({});
  };

  const updateFilterValue = ({ type, value }) => {
    const params = { [type]: value };
    updateFilters(params);
    updateSelectedUnit({});
  };

  const filters = useMemo(() => {
    const filterProps = [
      {
        isSearchEnabled: true,
        dsVersion: "2.0",
        options: creatorOptions,
        showSelectedOptionsOnTop: false,
        value: selectedFilters.creators,
        showAllOption: true,
        labelStyle: styles.labelStyle,
        label: t("common:created_by"),
        labelValue: getLabelValueForMultiFilterLabelButton({
          options: creatorOptions,
          selectedOptions: selectedFilters.creators,
          noneText: t("common:select"),
          allText: t("common:all"),
        }),
        onChange: creators =>
          updateFilterValue({ type: "creators", value: creators }),
      },
    ];

    if (!isDPLibrary) {
      filterProps.unshift({
        options: subjectOptions,
        isSearchEnabled: true,
        value: selectedFilters.subjects,
        showSelectedOptionsOnTop: false,
        showAllOption: true,
        labelStyle: styles.labelStyle,
        label: t("common:subject"),
        labelValue: getLabelValueForMultiFilterLabelButton({
          options: subjectOptions,
          selectedOptions: selectedFilters.subjects,
          noneText: t("common:select"),
          allText: t("common:all"),
        }),
        onChange: subjects =>
          updateFilterValue({ type: "subjects", value: subjects }),
      });

      if (!isDPSchoolLibrary) {
        filterProps.unshift({
          options: unitTypeOptions,
          value: selectedFilters.unitTypes,
          showSelectedOptionsOnTop: false,
          showAllOption: true,
          labelStyle: styles.labelStyle,
          label: t("common:unit_type_label"),
          labelValue: getLabelValueForMultiFilterLabelButton({
            options: unitTypeOptions,
            selectedOptions: selectedFilters.unitTypes,
            noneText: t("common:select"),
            allText: t("common:all"),
          }),
          onChange: unitTypes =>
            updateFilterValue({ type: "unitTypes", value: unitTypes }),
        });
      }
    } else {
      filterProps.unshift(coursesFilterPropOptions);
    }

    return filterProps;
  }, [selectedFilters, staffList, subjectList, unitTypes]);

  const searchInputComponent = (
    <SearchInput
      dsVersion="2.0"
      focusSearchBySlash={true}
      value={searchInput}
      placeholder={t("toddleLibrary:search_placeholder")}
      size={"medium"}
      onChange={onSearchTextChange}
      testId="app-components-unit-filters-le-import-to-unit"
    />
  );

  const renderFilters = () => {
    return _.map(
      filters,
      ({
        label,
        labelValue,
        isSearchEnabled,
        showSelectedOptionsOnTop,
        showAllOption,
        options,
        labelStyle,
        onChange,
        value,
      }) => {
        return (
          <ChecklistDropdown
            dsVersion="2.0"
            key={label}
            placement={"bottomRight"}
            onChange={onChange}
            isSearchEnabled={isSearchEnabled}
            options={options}
            value={value}
            showAllOption={showAllOption}
            showSelectedOptionsOnTop={showSelectedOptionsOnTop}
            testId={`appComponents-leIntoUnitImport-unitFilters-${_.camelCase(
              label
            )}`}
          >
            <FilterLabelButton
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              label={label}
              labelValue={labelValue}
              labelStyle={labelStyle}
              testId={`appComponents-leIntoUnitImport-unitFilters-${_.camelCase(
                label
              )}`}
            />
          </ChecklistDropdown>
        );
      }
    );
  };

  return (
    <div className={classes.container}>
      <div className={classes.searchContainer}>{searchInputComponent}</div>
      <div className={classes.filterContainer}>{renderFilters()}</div>
    </div>
  );
};

const mapActionCreators = {
  updateFilters,
  updateSelectedUnit,
};

const mapStateToProps = (state, ownProps) => {
  const {
    LEIntoUnitImport: { gradeId, curriculumType, filters: selectedFilters },
  } = state;
  const { academicYears } = ownProps;

  const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
  const orgId = platformUserInfo.org_id;
  const userId = platformUserInfo.id;

  const currentCurriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    ""
  );

  const isSchoolLibrary = isSchoolLibraryRoute();

  const isDPSchoolLibrary =
    currentCurriculumProgramType === CURRICULUM_TYPE_DP && isSchoolLibrary;
  const isDPLibrary = isDPLibraryRoute();

  let filteredUnitTypes = EMPTY_ARRAY;

  if (!isDPLibrary && !isDPSchoolLibrary) {
    const organizationDetails = getOrganizationDetailsFromCache(orgId);
    const unitTypes = _.get(organizationDetails, "unitTypes", []);
    filteredUnitTypes = getCurriculumUnitTypesMemoized({
      unitTypes,
      curriculumType,
    });
  }

  const subjectFilterInput = {
    grades: _.compact([gradeId]),
  };

  const orgConnectionFilterInput = {
    grades: _.compact([gradeId]),
    academicYearIds: !_.isEmpty(academicYears) ? academicYears : null,
  };

  const curriculumPrograms = getOrganizationCurriculumPrograms(state);
  const selectedCurriculumProgramId = getCurriculumProgramIdFromType({
    curriculumPrograms,
    curriculumProgramType: curriculumType,
  });

  const curriculumProgramsWithAllClassAccess = getCurriculumProgramsWithAllClassAccess(
    state
  );

  const hasAccessForAllClass = _.some(
    curriculumProgramsWithAllClassAccess,
    ({ id }) => id === selectedCurriculumProgramId
  );

  const hasLibrarySuperAdminAccess = checkHasLibrarySuperAdminAccess();

  const commonQueryVariables = {
    portalType: "PLANNER", // Need to pass this to send platform user JWT in request header
    filters: {
      grades: _.compact([gradeId]),
      academicYears: !_.isEmpty(academicYears) ? academicYears : null,
    },
  };

  const staffCoursesFilterInput = {
    id: userId,
    ...commonQueryVariables,
  };

  const orgCoursesFilterInput = {
    id: orgId,
    ...commonQueryVariables,
  };

  const shouldShowOrganizationCourses =
    hasLibrarySuperAdminAccess && hasAccessForAllClass;

  return {
    unitTypes: filteredUnitTypes,
    orgId,
    userId,
    gradeId,
    shouldShowOrganizationCourses,
    subjectFilterInput,
    orgConnectionFilterInput,
    staffCoursesFilterInput,
    orgCoursesFilterInput,
    curriculumProgramsWithAllClassAccess,
    isDPLibrary,
    isDPSchoolLibrary,
    selectedFilters,
  };
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getOrganizationSubjectsQuery, {
    name: "getOrganizationSubjects",
    skip: ({ isDPLibrary }) => isDPLibrary,
    options: ({ orgId, subjectFilterInput }) => ({
      variables: {
        id: orgId,
        filters: subjectFilterInput,
        portalType: "PLANNER",
      },
    }),
    props: ({ ownProps: { orgId, subjectFilterInput } }) => {
      const organizationSubjectDetails = getOrganizationSubjectsQueryFromCache({
        id: orgId,
        filters: subjectFilterInput,
      });

      const subjectList = _.get(organizationSubjectDetails, "subjects");

      return { subjectList };
    },
  }),
  graphql(getOrganizationStaffQuery, {
    name: "getOrganizationStaff",
    options: ({ orgId, orgConnectionFilterInput }) => ({
      variables: {
        id: orgId,
        filters: orgConnectionFilterInput,
        portalType: "PLANNER",
      },
    }),
    props: ({ ownProps: { orgId, orgConnectionFilterInput } }) => {
      const organizationStaffDetails = getOrganizationStaffQueryFromCache({
        id: orgId,
        filters: orgConnectionFilterInput,
      });

      const staffList = _.get(organizationStaffDetails, "staff.edges");

      return { staffList };
    },
  }),
  graphql(getStaffCoursesQuery, {
    name: "getStaffCourses",
    skip: ({ userId, shouldShowOrganizationCourses }) =>
      !userId || shouldShowOrganizationCourses,
    options: ({ staffCoursesFilterInput }) => ({
      fetchPolicy: "cache-and-network",
      variables: staffCoursesFilterInput,
    }),
    props({ getStaffCourses }) {
      const queryData = getStaffCoursesFromCache(getStaffCourses.variables);
      const courses = _.get(queryData, "coursesV2.edges");

      return {
        isData: !_.isEmpty(queryData),
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          getStaffCourses.networkStatus
        ),
        courses,
      };
    },
  }),
  graphql(getOrganizationCoursesQuery, {
    name: "getOrganizationCourses",
    skip: ({ orgId, shouldShowOrganizationCourses }) =>
      !orgId || !shouldShowOrganizationCourses,
    options: ({ orgCoursesFilterInput }) => ({
      fetchPolicy: "cache-and-network",
      variables: orgCoursesFilterInput,
    }),
    props({ getOrganizationCourses }) {
      const queryData = _.get(getOrganizationCourses, "node", {});
      const courses = _.get(queryData, "courses.edges");

      return {
        isData: !_.isEmpty(queryData),
        isLoading: _.includes(
          GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
          getOrganizationCourses.networkStatus
        ),
        courses,
      };
    },
  })
)(UnitFilters);
